<script setup lang="ts">
import { computed } from 'vue'
import RadarSpinner from '@/components/common/RadarSpinner.vue'

interface Props {
	variant?: 'primary' | 'secondary' | 'danger' | 'success'
	size?: 'small' | 'default' | 'large'
	disabled?: boolean
	loading?: boolean
	/** Font Awesome class rendered before the label (e.g. 'fa-solid fa-download') */
	icon?: string
}

const props = withDefaults(defineProps<Props>(), {
	variant: 'primary',
	size: 'default',
	disabled: false,
	loading: false,
	icon: undefined,
})

const color = computed(() => (props.variant === 'danger' ? 'error' : props.variant))
</script>

<template>
	<v-btn
		:color="color"
		:size="size"
		:disabled="disabled || loading"
		class="base-button"
		:class="{ 'base-button--loading': loading }"
		:aria-busy="loading || undefined"
	>
		<!-- Label stays in the layout (hidden, not removed) so the button keeps
         its width while the spinner crossfades in -->
		<span class="base-button__content">
			<i
				v-if="icon"
				:class="icon"
				class="base-button__icon"
			/>
			<slot name="icon-left" />
			<slot />
			<slot name="icon-right" />
		</span>
		<Transition name="fade">
			<span
				v-if="loading"
				class="base-button__spinner"
			>
				<RadarSpinner :size="18" />
			</span>
		</Transition>
	</v-btn>
</template>

<style scoped lang="scss">
.base-button {
	&__content {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		transition: opacity $duration-fast $ease-standard;
	}

	&--loading .base-button__content {
		opacity: 0;
	}

	&__icon {
		font-size: 0.875em;
	}

	&__spinner {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;

		// Spinner inherits the button's text color so it reads on any variant
		:deep(.radar-spinner) {
			--radar-ring: currentColor;
			--radar-core: currentColor;
		}
	}
}
</style>
